import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { getCreatedToastMessage, getErrorToastMessage } from '@/libs/api'
import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type { LeaveItem } from '@/services/api'
import { leavesApi } from '@/services/api'

export function useLeaves(storeID: string, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.leaves.list(storeID),
    queryFn: () => leavesApi.list(storeID),
    enabled: enabled && !!storeID,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useLeave(
  params: {
    employeeID: string
    storeID: string
    leaveStart: string
    leaveEnd: string
  },
  enabled = true,
) {
  return useQuery({
    queryKey: QUERY_KEYS.leaves.id(params.employeeID),
    queryFn: () => leavesApi.getByRange(params),
    enabled: enabled && !!params.employeeID && !!params.storeID,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useCreateLeave() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({
      params,
      leaves,
    }: {
      params: { employeeID: string; storeID: string }
      leaves: LeaveItem[]
    }) => leavesApi.create(params, leaves),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.leaves.all })
      toast.success(getCreatedToastMessage(result, t('leaves.requestSubmitted')))
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useDeleteLeave() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({
      params,
      leaves,
    }: {
      params: { employeeID: string; storeID: string }
      leaves: LeaveItem[]
    }) => leavesApi.delete(params, leaves),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.leaves.all })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}
